import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import qs from 'qs';

import { getFormValues, submit, isSubmitting } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import Progress from 'components/common/Progress';
import Dialog, {
  DialogContent,
  DialogTitle,
  DialogActions,
} from 'components/common/Dialog';
import Button from 'components/common/Button';

import { fetchPatients, clearPatients } from 'redux/modules/patients';
import { checkPatientInsuranceEligibility } from 'redux/modules/patientsInsurance';
import { downloadPatientDocument } from 'redux/modules/patientsDocuments';
import { fetchTemplates } from 'redux/modules/templates';
import { fetchBusiness } from 'redux/modules/business';
import { fetchSystemSettings } from 'redux/modules/systemSettings';

import throttle from 'utils/throttle';

import InsuranceCreateEditForm from 'components/patient/InsuranceCreateEditDialog/InsuranceCreateEditForm';
import PrintDialogForm from 'components/common/PrintDialog/PrintDialogForm';

import PatientsDialogFilterForm from './PatientsDialogFilterForm';
import PatientsDialogList from './PatientsDialogList';
import PatientsDialogDocumentsUploadForm from './PatientsDialogDocumentsUploadForm';
import PatientsDialogInsurancesList from './PatientsDialogInsurancesList';
import PatientsDialogDocumentsList from './PatientsDialogDocumentsList';

const styles = () => ({
  action: {
    '&:first-child': {
      position: 'absolute',
      left: 14,
      top: 8,
    },
  },
  insuranceCreateForm: {
    padding: '15px 171px',
  },
});

class PatientsDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      limit: 10,
      offset: 0,
      createInsuranceView: false,
      autosuggestFieldFocused: false,
      triggerTemplatesLoading: false,
      templatesLimit: 10,
      templatesOffset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onDialogEnter = this.onDialogEnter.bind(this);
    this.onDialogExited = this.onDialogExited.bind(this);
    this.onPatientsScrollFetchRequest = this.onPatientsScrollFetchRequest.bind(this);
    this.onInsuranceClick = this.onInsuranceClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onCreateInsuranceClick = this.onCreateInsuranceClick.bind(this);
    this.onBackButtonClick = this.onBackButtonClick.bind(this);
    this.onInsuranceCreateFormSubmitSuccess = this.onInsuranceCreateFormSubmitSuccess.bind(this);
    this.onAutosuggestFieldFocus = this.onAutosuggestFieldFocus.bind(this);
    this.onAutosuggestFieldBlur = this.onAutosuggestFieldBlur.bind(this);
    this.onTemplatesRequest = this.onTemplatesRequest.bind(this);
    this.onTemplatesScrollFetchRequest = this.onTemplatesScrollFetchRequest.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillReceiveProps(nextProps) {
    const {
      open,
      filters,
      loading,
      selectedPatient,
      templatesCount,
      printDataLoading,
    } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters['Name or PRN'] !== filters['Name or PRN'];
    const newState = {};

    if (filtersChanged) {
      newState.offset = 0;
    } else if (open
      && !selectedPatient
      && loading === 1
      && nextProps.loading === 0
      && this.scrollbarsRef
      && this.scrollbarsRef.getScrollHeight() <= this.scrollbarsRef.getClientHeight()
      && (nextProps.patients.length - limit) >= offset) {
      newState.offset = offset + limit;
    }

    if (templatesCount === 0 || !open) {
      newState.triggerTemplatesLoading = true;
    } else {
      newState.triggerTemplatesLoading = false;
    }
    if (open && nextProps.printDataLoading < printDataLoading) {
      newState.triggerTemplatesLoading = false;
    }

    this.setState(newState);
  }

  componentDidUpdate(prevProps, prevState) {
    const { open, filters, selectedPatient, variant } = this.props;
    const { limit, offset, templatesLimit, templatesOffset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const templatesLimitChanged = prevState.templatesLimit !== templatesLimit;
    const templatesOffsetChanged = prevState.templatesOffset !== templatesOffset;
    const throttledFiltersChanged =
      filters['Name or PRN']
      && filters['Name or PRN'].length > 0
      && prevProps.filters['Name or PRN'] !== filters['Name or PRN'];
    const filtersChanged = (!filters['Name or PRN']
      && prevProps.filters['Name or PRN']
      && prevProps.filters['Name or PRN'].length > 0
      && prevProps.filters['Name or PRN'] !== filters['Name or PRN']);
    if (open) {
      if (throttledFiltersChanged) {
        this.onThrottledRequest();
      } else if (filtersChanged || limitChanged || offsetChanged) {
        this.onRequest();
      }
      if (variant === 'forms' && selectedPatient) {
        if (!prevProps.selectedPatient) {
          this.onTemplatesRequest();
          this.props.actions.fetchBusiness({
            triggerLoading: true,
          });
          this.props.actions.fetchSystemSettings({
            triggerLoading: true,
          });
        } else if (templatesLimitChanged || templatesOffsetChanged) {
          this.onTemplatesRequest();
        }
      }
      if (variant === 'documents'
        && selectedPatient
        && !prevProps.selectedPatient) {
        this.props.actions.fetchBusiness({
          triggerLoading: true,
        });
      }
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map((key) => {
        let name = key;
        let operation = 'startswith';
        let caseSensitivity = null;
        switch (key) {
          case 'Name or PRN':
            if (/^\d+$/.test(filters[key])) {
              name = 'Record Number';
              operation = 'eq';
            } else {
              name = 'Full Name';
              caseSensitivity = false;
            }
            break;
          default:
            caseSensitivity = false;
        }
        return {
          name,
          operation,
          val: filters[key],
          ...caseSensitivity !== null ? { case_sensitivity: caseSensitivity } : {},
        };
      })
      : null;
    actions.fetchPatients({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onTemplatesRequest() {
    const { actions } = this.props;
    const { templatesLimit, templatesOffset, triggerTemplatesLoading } = this.state;
    actions.fetchTemplates({
      limit: templatesLimit,
      offset: templatesOffset,
      triggerLoading: triggerTemplatesLoading,
      push: templatesOffset > 0,
    });
  }

  onTemplatesScrollFetchRequest() {
    const { templatesOffset, templatesLimit } = this.state;
    if ((this.props.templates.length - templatesLimit) >= templatesOffset) {
      this.setState({ templatesOffset: templatesOffset + templatesLimit });
    }
  }

  onDialogEnter() {
    this.props.actions.clearPatients();
    this.onRequest();
  }

  onDialogExited() {
    this.setState({
      offset: 0,
      createInsuranceView: false,
    });
  }

  onPatientsScrollFetchRequest() {
    const { offset, limit } = this.state;
    if ((this.props.patients.length - limit) >= offset) {
      this.setState({ offset: offset + limit });
    }
  }

  onInsuranceClick(insurance) {
    const { actions, selectedPatient } = this.props;
    actions.checkPatientInsuranceEligibility({
      'Patient ID': selectedPatient['Record Number'],
      'Insurance ID': insurance.ID,
    });
  }

  onDocumentClick(document) {
    this.props.actions.downloadPatientDocument({
      href: process.env.REACT_APP_API_ROOT
        ? `${process.env.REACT_APP_API_ROOT}${document['File Url']}${document['File Name']}`
        : `${document['File Url']}${document['File Name']}`,
    });
  }

  onCreateInsuranceClick() {
    this.setState({ createInsuranceView: true });
  }

  onBackButtonClick() {
    if (this.state.createInsuranceView) {
      this.setState({ createInsuranceView: false });
    } else {
      this.props.onPatientClear();
    }
  }

  onInsuranceCreateFormSubmitSuccess(values) {
    this.setState({ createInsuranceView: false });
    this.props.onInsuranceCreate(values);
  }

  onAutosuggestFieldFocus() {
    this.setState({ autosuggestFieldFocused: true });
  }

  onAutosuggestFieldBlur() {
    this.setState({ autosuggestFieldFocused: false });
  }

  render() {
    const {
      open,
      onClose,
      patients,
      onPatientClick,
      selectedPatient,
      loading,
      variant,
      onDocumentCreate,
      actions,
      printDataLoading,
      templates,
      business,
      systemSettings,
      printFormValues,
      submittingPrintForm,
      submittingInsuranceCreateForm,
      onSign,
      appInfo,
      classes,
    } = this.props;
    const { createInsuranceView, autosuggestFieldFocused } = this.state;
    const buttonPrintAllDisabled = !Object.keys(printFormValues)
      .find(key => printFormValues[key] === true);
    const editPreviewItems = Object.keys(printFormValues)
      .reduce((accumulator, currentValue) => {
        if (printFormValues[currentValue] === true) {
          if (currentValue.indexOf('Document') !== -1) {
            const documentID = Number(currentValue.split('_')[1]);
            const orderLineIDs = currentValue.split('_')[2];
            return {
              ...accumulator,
              ...orderLineIDs
                ? { [`document-${documentID}`]: [
                  ...accumulator[`document-${documentID}`] || [],
                  orderLineIDs,
                ] }
                : { [`document-${documentID}`]: null },
            };
          }
          if (currentValue.indexOf('Template') !== -1) {
            const templateID = Number(currentValue.split('_')[1]);
            return {
              ...accumulator,
              ...{ [`template-${templateID}`]: null },
            };
          }
          return accumulator;
        }
        return accumulator;
      }, {});
    const buttonEditPreviewDisabled = Object.keys(editPreviewItems).length === 0;
    const editPreviewHrefParams = qs.stringify(editPreviewItems, {
      encode: false, addQueryPrefix: true,
    });
    const editPreviewHrefPrefix = selectedPatient
      ? `/charts/${selectedPatient['Record Number']}/print` : '';
    let editPreviewHrefStem = '';
    if (Object.keys(editPreviewItems).join().indexOf('document') !== -1) {
      const firstDocumentValue = Object.keys(editPreviewItems)
        .find(item => item.indexOf('document') !== -1);
      const firstDocumentID = firstDocumentValue.replace('document-', '');
      const orderLine = editPreviewItems[firstDocumentValue];
      editPreviewHrefStem = orderLine
        ? `/document/${firstDocumentID}/${orderLine[0]}`
        : `/document/${firstDocumentID}`;
    } else if (Object.keys(editPreviewItems).join().indexOf('template') !== -1) {
      editPreviewHrefStem =
        `/template/${Object.keys(editPreviewItems).find(item => item.indexOf('template') !== -1).replace('template-', '')}`;
    }
    const editPreviewHref = `${editPreviewHrefPrefix}${editPreviewHrefStem}${editPreviewHrefParams}`;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        onEnter={this.onDialogEnter}
        onExited={this.onDialogExited}
        maxWidth="md"
      >
        <DialogTitle
          onCloseClick={onClose}
          {...selectedPatient ? { onBackClick: this.onBackButtonClick } : {}}
        >
          <Typography
            variant="title"
            color="secondary"
            align="center"
          >
            {(variant === 'insurances' && !selectedPatient) && 'Choose Patient'}
            {(variant === 'insurances' && selectedPatient && !createInsuranceView)
              && `${selectedPatient['First Name']}, ${selectedPatient['Last Name']}: Choose Insurance`}
            {(variant === 'insurances' && selectedPatient && createInsuranceView)
              && `${selectedPatient['First Name']}, ${selectedPatient['Last Name']}: Create Insurance`}
            {(variant === 'documents' && !selectedPatient) && 'Choose Patient'}
            {(variant === 'documents' && selectedPatient) && 'Choose Document'}
            {(variant === 'forms' && !selectedPatient) && 'Choose Patient'}
            {(variant === 'forms' && selectedPatient)
                && `${selectedPatient['First Name']}, ${selectedPatient['Last Name']}: Print Data`}
          </Typography>
        </DialogTitle>
        {(variant === 'documents' && selectedPatient) &&
          <PatientsDialogDocumentsUploadForm
            patient={selectedPatient}
            onSubmitSuccess={onDocumentCreate}
          />
        }
        {!selectedPatient &&
          <PatientsDialogFilterForm form="chartsFilter" />
        }
        <DialogContent
          withScrollbars
          disableGutters
          fullHeight
          scrollbarsRef={(scrollbarsRef) => {
            this.scrollbarsRef = scrollbarsRef;
          }}
          onScrollFrame={(event) => {
            if (event.top > 0.8) {
              if (!selectedPatient) {
                this.onPatientsScrollFetchRequest();
              } else if (selectedPatient && variant === 'forms') {
                this.onTemplatesScrollFetchRequest();
              }
            }
          }}
          scrollMaxHeightDecrease={selectedPatient ? 57 : 80}
          {...variant === 'documents' && selectedPatient ? { scrollMaxHeightDecrease: 169 } : {}}
          disableMouseScroll={autosuggestFieldFocused}
        >
          {!selectedPatient &&
            <PatientsDialogList
              patients={patients}
              onPatientClick={onPatientClick}
              loading={loading}
            />
          }
          {(selectedPatient && variant === 'insurances' && !createInsuranceView) &&
            <PatientsDialogInsurancesList
              insurances={selectedPatient['Insurance Information'].filter(item =>
                item['Insurance Info']['Change Info']
                && item['Insurance Info']['Change Info']['Is Eligibility'])}
              onInsuranceClick={this.onInsuranceClick}
            />
          }
          {(selectedPatient && variant === 'insurances' && createInsuranceView) &&
            <InsuranceCreateEditForm
              className={classes.insuranceCreateForm}
              onSubmitSuccess={this.onInsuranceCreateFormSubmitSuccess}
              form="patientInsuranceCreateForm"
              initialValues={{
                'Copay Type': 'None',
                'Insurance Status': 'Active',
                'Relation to Insured': 'Self',
                'Signature On File': true,
              }}
              {...{ 'User Record Number': selectedPatient['Record Number'] }}
              onAutosuggestFieldFocus={this.onAutosuggestFieldFocus}
              onAutosuggestFieldBlur={this.onAutosuggestFieldBlur}
            />
          }
          {(selectedPatient && variant === 'forms' && printDataLoading === 0) &&
            <PrintDialogForm
              form={`${selectedPatient['Record Number']}-printForm`}
              templates={templates}
              patient={selectedPatient}
              business={business}
              systemSettings={systemSettings}
            />
          }
          {(selectedPatient && variant === 'documents') &&
            <PatientsDialogDocumentsList
              documents={selectedPatient.Documents}
              onDocumentClick={this.onDocumentClick}
              business={business}
            />
          }
        </DialogContent>
        {(loading > 0 || printDataLoading > 0) &&
          <Progress />
        }
        {selectedPatient &&
          <DialogActions
            {...(variant === 'insurances' && !createInsuranceView)
            || variant === 'forms' ? {
                classes: { action: classes.action },
              } : {}}
          >
            {(variant === 'insurances' && !createInsuranceView) &&
              <Button
                onClick={this.onCreateInsuranceClick}
                color="primary"
                underline
              >
                Add New Insurance
              </Button>
            }
            {(variant === 'forms') &&
              <Button
                color="primary"
                underline
                disabled={buttonEditPreviewDisabled}
                href={editPreviewHref}
              >
                Edit / Preview
              </Button>
            }
            {(variant !== 'forms') &&
              <Button
                onClick={onClose}
                variant="raised"
              >
                Cancel
              </Button>
            }
            {(variant === 'forms') &&
              <Button
                onClick={() => onSign(printFormValues)}
                disabled={buttonPrintAllDisabled}
                variant="raised"
              >
                Sign
              </Button>
            }
            {((variant === 'insurances' && !createInsuranceView) || variant === 'documents') &&
              <Button
                href={`/charts/${selectedPatient['Record Number']}`}
                variant="raised"
                color="primary"
              >
                See Profile
              </Button>
            }
            {(variant === 'insurances' && createInsuranceView) &&
              <Button
                onClick={() => {
                  actions.submit('patientInsuranceCreateForm');
                }}
                variant="raised"
                color="primary"
                disabled={submittingInsuranceCreateForm}
              >
                Save
              </Button>
            }
            {(variant === 'forms') &&
              <Button
                onClick={() => {
                  actions.submit(`${selectedPatient['Record Number']}-printForm`);
                }}
                variant="raised"
                color="primary"
                disabled={buttonPrintAllDisabled || submittingPrintForm}
              >
                Print All
              </Button>
            }
          </DialogActions>
        }
      </Dialog>
    );
  }
}

PatientsDialog.propTypes = {
  variant: PropTypes.oneOf(['insurances', 'documents', 'forms']),
  patients: PropTypes.arrayOf(PropTypes.shape({
    'First Name': PropTypes.string,
  })).isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  loading: PropTypes.number.isRequired,
  onPatientClick: PropTypes.func,
  onPatientClear: PropTypes.func,
  selectedPatient: PropTypes.shape({
    'First Name': PropTypes.string,
  }),
  actions: PropTypes.shape({
    fetchPatients: PropTypes.func.isRequired,
    clearPatients: PropTypes.func.isRequired,
    checkPatientInsuranceEligibility: PropTypes.func.isRequired,
    downloadPatientDocument: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
    fetchSystemSettings: PropTypes.func.isRequired,
    fetchTemplates: PropTypes.func.isRequired,
  }).isRequired,
  filters: PropTypes.shape({
    'Name or PRN': PropTypes.string,
  }).isRequired,
  onDocumentCreate: PropTypes.func,
  onInsuranceCreate: PropTypes.func,
  templates: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  templatesCount: PropTypes.number.isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object.isRequired,
  printDataLoading: PropTypes.number.isRequired,
  printFormValues: PropTypes.shape({
    Data: PropTypes.object,
    Documents: PropTypes.object,
    Templates: PropTypes.object,
  }),
  submittingPrintForm: PropTypes.bool.isRequired,
  submittingInsuranceCreateForm: PropTypes.bool.isRequired,
  onSign: PropTypes.func,
  classes: PropTypes.shape({
    action: PropTypes.string.isRequired,
    insuranceCreateForm: PropTypes.string.isRequired,
  }).isRequired,
};

PatientsDialog.defaultProps = {
  variant: 'insurances',
  open: false,
  onClose: () => {},
  onPatientClick: () => {},
  onPatientClear: () => {},
  onDocumentCreate: () => {},
  onInsuranceCreate: () => {},
  selectedPatient: null,
  business: {},
  printFormValues: {},
  onSign: () => {},
};

const mapStateToProps = (store, ownProps) => {
  const chartsFilterValues = getFormValues('chartsFilter')(store);
  const printFormValues = ownProps.selectedPatient
    ? getFormValues(`${ownProps.selectedPatient['Record Number']}-printForm`)(store) : {};
  return {
    patients: store.patients.data,
    count: store.patients.count,
    loading: store.patients.loading,
    filters: {
      ...chartsFilterValues
        && chartsFilterValues['Name or PRN']
        ? { 'Name or PRN': chartsFilterValues['Name or PRN'] }
        : {},
    },
    templates: store.templates.data,
    templatesCount: store.templates.count,
    business: store.business.data,
    systemSettings: store.systemSettings.data,
    printDataLoading: store.templates.loading
      + store.business.loading
      + store.systemSettings.loading,
    printFormValues,
    submittingPrintForm: ownProps.selectedPatient
      ? isSubmitting(`${ownProps.selectedPatient['Record Number']}-printForm`)(store) : false,
    submittingInsuranceCreateForm: ownProps.selectedPatient
      ? isSubmitting('patientInsuranceCreateForm')(store) : false,
    appInfo: store.appInfo,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchPatients,
    clearPatients,
    checkPatientInsuranceEligibility,
    downloadPatientDocument,
    submit,
    fetchBusiness,
    fetchSystemSettings,
    fetchTemplates,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(PatientsDialog),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientsDialog/PatientsDialog.js